class FreeGiftOffer extends HTMLElement {
  connectedCallback() {
    this.config = JSON.parse(this.querySelector('[data-gift-config]').textContent);
    this.dialog = this.querySelector('dialog');
    this.error = this.querySelector('[data-gift-error]');
    this.onClick = this.handleClick.bind(this);
    this.onCancel = (event) => {
      event.preventDefault();
      if (!this.busy) this.finish();
    };
    this.onCheckout = this.guardCheckout.bind(this);
    this.addEventListener('click', this.onClick);
    this.dialog.addEventListener('cancel', this.onCancel);
    this.dialog.addEventListener('close', this.onClose = () => {
      document.body.classList.remove('free-gift-open');
      if (this.resolveOffer) this.finish();
    });
    document.addEventListener('click', this.onCheckout, true);
    document.addEventListener('submit', this.onCheckout, true);
    if (this.config.preview && this.cards.length) this.open();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.dialog.removeEventListener('cancel', this.onCancel);
    this.dialog.removeEventListener('close', this.onClose);
    document.removeEventListener('click', this.onCheckout, true);
    document.removeEventListener('submit', this.onCheckout, true);
    this.finish();
  }

  get cards() {
    return [...this.querySelectorAll('[data-gift-card]')];
  }

  isGift(item) {
    return item.properties?._free_gift_offer === 'v1';
  }

  isGiftProduct(item) {
    return (this.config.giftProducts || []).includes(item.product_id);
  }

  isQualifying(item) {
    if (this.isGift(item) || item.parent_relationship || item.original_price <= 0) return false;
    if (this.config.anyProduct) return !this.isGiftProduct(item);
    return this.config.eligibleProducts.includes(item.product_id);
  }

  qualifyingSubtotal(cart) {
    return cart.items
      .filter((item) => !this.isGift(item) && !this.isGiftProduct(item))
      .reduce((sum, item) => sum + (item.final_line_price || 0), 0);
  }

  meetsThreshold(cart) {
    const minimum = (this.config.minSubtotal || 0) * Number(window.Shopify?.currency?.rate || 1);
    return this.qualifyingSubtotal(cart) >= minimum;
  }

  async request(path, body) {
    const response = await fetch(`${window.Shopify.routes.root}${path}`, {
      ...(body ? { method: 'POST', body: JSON.stringify(body) } : {}),
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    });
    const data = await response.json();
    if (!response.ok || data.status || data.errors) throw new Error(this.config.error);
    return data;
  }

  findParent(cart, added) {
    return cart.items.find((item) => item.key === added.key && this.isQualifying(item)) ||
      cart.items.find((item) => this.isQualifying(item) && item.variant_id === added.variant_id &&
        JSON.stringify(item.properties || {}) === JSON.stringify(added.properties || {}) &&
        item.selling_plan_allocation?.selling_plan?.id === added.selling_plan_allocation?.selling_plan?.id);
  }

  async offerAfterAdd(added, productForm) {
    if (!this.config.enabled || !this.cards.length || this.resolveOffer || this.dialog.open) return added;
    let cart;
    try {
      cart = await this.request('cart.js');
    } catch {
      return added;
    }
    const parent = this.findParent(cart, added);
    if (!parent || cart.items.some((item) => this.isGift(item)) || !this.meetsThreshold(cart)) return added;
    this.cards.forEach((card) => { card.hidden = Number(card.dataset.productId) === parent.product_id; });
    if (!this.cards.some((card) => !card.hidden)) return added;
    this.parent = parent;
    this.added = added;
    const quickAdd = productForm?.closest('quick-add-modal');
    this.activeElement = quickAdd?.openedBy || productForm?.submitButton;
    if (quickAdd) quickAdd.hide(true);
    if (typeof productForm?.cart?.close === 'function') productForm.cart.close();
    return new Promise((resolve) => {
      this.resolveOffer = resolve;
      this.open();
    });
  }

  open() {
    this.error.hidden = true;
    this.dialog.showModal();
    document.body.classList.add('free-gift-open');
  }

  finish(response = this.added) {
    const resolve = this.resolveOffer;
    this.resolveOffer = null;
    if (this.dialog.open) this.dialog.close();
    document.body.classList.remove('free-gift-open');
    this.activeElement?.focus();
    if (resolve) resolve(response);
  }

  setBusy(busy, button) {
    this.busy = busy;
    this.querySelectorAll('button, select').forEach((element) => { element.disabled = busy; });
    button?.setAttribute('aria-busy', String(busy));
  }

  showError(message) {
    this.error.textContent = message;
    this.error.hidden = false;
  }

  handleClick(event) {
    if (event.target === this.dialog && !this.busy) {
      const rect = this.dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.finish();
    }
    if (event.target.closest('[data-gift-close]') && !this.busy) this.finish();
    const button = event.target.closest('[data-gift-accept]');
    if (button && !this.busy) this.accept(button);
  }

  async removeAttempt(token) {
    const cart = await this.request('cart.js');
    for (const item of cart.items.filter((line) => line.properties?._free_gift_token === token)) {
      await this.request('cart/change.js', { id: item.key, quantity: 0 });
    }
    this.pendingToken = null;
  }

  async accept(button) {
    if (this.config.preview && !this.resolveOffer) {
      this.showError(this.config.previewMessage);
      return;
    }
    this.setBusy(true, button);
    this.error.hidden = true;
    try {
      if (this.pendingToken) await this.removeAttempt(this.pendingToken);
      const cart = await this.request('cart.js');
      const parent = this.findParent(cart, this.parent);
      if (!parent) throw new Error(this.config.unavailable);
      if (!this.meetsThreshold(cart)) throw new Error(this.config.belowMinimum);
      if (cart.items.some((item) => this.isGift(item))) {
        window.location.assign(window.routes.cart_url);
        return;
      }
      const card = button.closest('[data-gift-card]');
      const variantId = Number(card.querySelector('[data-gift-variant]')?.value || button.dataset.variantId);
      this.pendingToken = crypto.randomUUID();
      const cartUI = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const response = await this.request('cart/add.js', {
        items: [{
          id: variantId,
          quantity: 1,
          parent_line_key: parent.key,
          properties: { _free_gift_offer: 'v1', _free_gift_token: this.pendingToken },
        }],
        ...(cartUI ? { sections: cartUI.getSectionsToRender().map((section) => section.id) } : {}),
        sections_url: window.location.pathname,
      });
      const updatedCart = await this.request('cart.js');
      const gift = updatedCart.items.find((item) => item.properties?._free_gift_token === this.pendingToken);
      const giftParent = updatedCart.items.find((item) => item.key === gift?.parent_relationship?.parent_key);
      if (!gift || !giftParent || !this.isQualifying(giftParent)) throw new Error(this.config.unavailable);
      if (gift.final_line_price !== 0 || gift.quantity !== 1) throw new Error(this.config.notFree);
      this.pendingToken = null;
      this.finish({ ...response.items[0], sections: response.sections });
    } catch (error) {
      if (this.pendingToken) {
        try {
          await this.removeAttempt(this.pendingToken);
        } catch {
          this.showError(this.config.error);
          return;
        }
      }
      const message = [this.config.unavailable, this.config.notFree, this.config.belowMinimum].includes(error.message) ? error.message : this.config.error;
      this.showError(message);
    } finally {
      this.setBusy(false, button);
    }
  }

  async validateCart() {
    if (this.pendingToken) await this.removeAttempt(this.pendingToken);
    let cart = await this.request('cart.js');
    const qualifies = this.meetsThreshold(cart);
    const invalid = cart.items.filter((item) => {
      if (!this.isGift(item)) return false;
      if (!qualifies) return true;
      const parent = cart.items.find((line) => line.key === item.parent_relationship?.parent_key);
      return !parent || !this.isQualifying(parent) || item.quantity !== 1 || item.final_line_price !== 0;
    });
    for (const item of invalid) {
      cart = await this.request('cart/change.js', { id: item.key, quantity: 0 });
    }
    return invalid.length === 0;
  }

  async guardCheckout(event) {
    if (!this.config.enabled && !this.config.hasGift && !this.pendingToken) return;
    const target = event.type === 'submit' ? event.submitter : event.target.closest('button[name="checkout"], input[name="checkout"], a[href]');
    if (!target || (target.tagName !== 'A' && target.name !== 'checkout') ||
      (target.tagName === 'A' && !/\/checkout(?:[/?#]|$)/.test(target.getAttribute('href')))) return;
    if (this.checkoutAllowed === target) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.busy || this.checkingOut) return;
    this.checkingOut = true;
    try {
      if (await this.validateCart()) {
        this.checkoutAllowed = target;
        if (event.type === 'submit') target.form.requestSubmit(target);
        else target.click();
        this.checkoutAllowed = null;
      } else {
        window.location.assign(window.routes.cart_url);
      }
    } catch {
      const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
      if (errors) errors.textContent = this.config.error;
      else {
        const checkoutError = this.querySelector('[data-gift-checkout-error]');
        checkoutError.textContent = this.config.error;
        checkoutError.hidden = false;
      }
    } finally {
      this.checkingOut = false;
    }
  }
}

if (!customElements.get('free-gift-offer')) customElements.define('free-gift-offer', FreeGiftOffer);
